import React from 'react'
import moment from 'moment'

import { GrouppedRoute, AirportDetailsType } from 'utils/types'
import {
  Container,
  List,
  SecondaryText,
  WarningContainer,
  WarningText,
  LogosWrapper,
  InnerContainer,
  Heading,
  BackIconContainer,
} from './Styles'
import { ReactComponent as Overnight } from 'assets/images/Overnight.svg'
import { ReactComponent as Transfer } from 'assets/images/Transfer.svg'
import { ReactComponent as WeightArrowLeft } from 'assets/images/WeightArrowLeft.svg'
import FlightItem from './FlightItem'
import { getFormattedDayMinutesHours, getFormattedMinutesHours } from 'utils/utils'
import FlightLogo from './FlightLogo'

interface Props {
  airports: AirportDetailsType[]
  route: GrouppedRoute
  handleClose: () => void
}

const FlightsAside = ({ route, airports, handleClose }: Props) => {
  const createRouteDetails = React.useCallback((route: GrouppedRoute) => {
    const { flights, groupMinMaxTime, id, groupSize } = route
    const departsAt = moment(flights[0].departs_at).format('HH:mm')
    const arrivesAt = moment(flights[flights.length - 1].arrives_at).format('HH:mm')
    const [min, max] = groupMinMaxTime

    const groupSizeEquelsOneTimeFormat = `${departsAt}–${arrivesAt} • ${getFormattedDayMinutesHours(
      min
    )}`
    const groupsSizeMoreTimeFormat = max
      ? `Ranging: ${getFormattedMinutesHours(min)} - ${getFormattedMinutesHours(max)}`
      : `Duration: ${getFormattedMinutesHours(min)}`

    const flightsWithTransfers = flights.map((flight) => {
      if (flight.from) return flight
      const [from, to] = flight.code.split('-')
      return {
        ...flight,
        from,
        to,
      }
    })

    return {
      id,
      flights: flightsWithTransfers,
      formattedTime: groupSize - 1 ? groupsSizeMoreTimeFormat : groupSizeEquelsOneTimeFormat,
    }
  }, [])

  const airportsRecord = React.useMemo(
    () => [...route.path].map(({ name }) => airports.find(({ iata }) => iata === name)!),
    [airports, route]
  )
  const routeDetails = React.useMemo(() => createRouteDetails(route), [createRouteDetails, route])

  return (
    <Container>
      <InnerContainer>
        <BackIconContainer onClick={handleClose}>
          <WeightArrowLeft />
        </BackIconContainer>
        <Heading>
          {route.groupSize} flight {route.groupSize > 1 ? 'options' : 'option'}
        </Heading>
        <SecondaryText>{routeDetails.formattedTime}</SecondaryText>
        <List>
          {airportsRecord.map((airport) => {
            const flight = routeDetails.flights.find((flight) => flight.from === airport.iata)
            return (
              <FlightItem item={airport} key={airport.name}>
                {flight?.overnight ? (
                  <WarningContainer>
                    <Overnight style={{ marginRight: '1.2rem' }} />
                    <WarningText>
                      {route.groupSize > 1 ? 'Only with overnight stop' : 'Overnight stop'}
                    </WarningText>
                  </WarningContainer>
                ) : null}
                {flight?.distance === null ? (
                  <WarningContainer>
                    <Transfer style={{ marginRight: '1.2rem' }} />
                    <WarningText>Airport transfer</WarningText>
                  </WarningContainer>
                ) : null}
                {flight?.carriers.length ? (
                  <LogosWrapper>
                    {flight.carriers.map(({ carrier, carrier_name }, i) => (
                      <FlightLogo key={i} carrier={carrier} carrierName={carrier_name} />
                    ))}
                  </LogosWrapper>
                ) : null}
              </FlightItem>
            )
          })}
        </List>
      </InnerContainer>
    </Container>
  )
}

export default React.memo(FlightsAside)
